import { Badge } from "@hortis/ui/badge";
import { Button, IconButton } from "@hortis/ui/button";
import { DialogTrigger } from "@hortis/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@hortis/ui/dropdown-menu";
import { FormField } from "@hortis/ui/form";
import {
  DotsHorizontal,
  Hash2,
  IpenNumber,
  MessageCheckSquare,
} from "@hortis/ui/icons";
import { Tooltip, TooltipContent, TooltipTrigger } from "@hortis/ui/tooltip";
import { AccessionNumberFormat } from "generated/graphql";
import { Fragment, useState } from "react";
import { useFormContext } from "react-hook-form";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import { twMerge } from "tailwind-merge";
import { AccessionNumberDialog } from "./accession-number-dialog";
import {
  IdentificationDialog,
  useIdentificationFieldCount,
} from "./identification-dialog";
import { IpenNumberDialog } from "./ipen-number-dialog";
import type { AccessionDataZod } from "./schema";

export const AccessionFieldsMoreDropdown = () => {
  const [ipenNumberDialogOpen, setIpenNumberDialogOpen] = useState(false);
  const [accessionNumberDialogOpen, setAccessionNumberDialogOpen] =
    useState(false);
  const [identificationDialogOpen, setIdentificationDialogOpen] =
    useState(false);
  const form = useFormContext<Partial<AccessionDataZod>>();
  const accessionDate = form.watch("accessionDate");
  const accessionFormat = form.watch("accessionNumberFormat");
  const accessionSuffix = form.watch("accessionNumberSuffix");
  const exogenousAccessionNumber = form.watch("exogenousAccessionNumber");

  const getFormattedPrefixSuffix = () => {
    if (!accessionDate || accessionSuffix == null) {
      return null;
    }
    const year = accessionDate.getFullYear();
    const paddedSuffix = String(accessionSuffix).padStart(4, "0");
    return `${year}-${paddedSuffix}`;
  };

  const accessionNumber =
    accessionFormat === AccessionNumberFormat.Exogenous
      ? exogenousAccessionNumber
      : accessionFormat === AccessionNumberFormat.PrefixSuffix
        ? getFormattedPrefixSuffix()
        : null;

  const ipenNumber = form.watch("ipenNumber");
  const identificationDetails = form.watch("identificationDetails");
  const identificationFieldCount = useIdentificationFieldCount();

  const showIpenNumberOption = ipenNumber == null;
  const showAccessionNumberItem = accessionNumber == null;
  const showIdentificationItem = identificationDetails == null;

  const menuItems = [
    {
      visible: showAccessionNumberItem,
      content: (
        <DropdownMenuItem
          onSelect={() => {
            setAccessionNumberDialogOpen(true);
          }}
          testId="accession-number-menu-item"
        >
          <Hash2 className="h-4 w-4" />
          Set accession number
        </DropdownMenuItem>
      ),
    },
    {
      visible: showIpenNumberOption,
      content: (
        <DropdownMenuItem
          onSelect={() => {
            setIpenNumberDialogOpen(true);
          }}
          testId="ipen-number-menu-item"
        >
          <IpenNumber className="h-4 w-4" />
          Set IPEN number
        </DropdownMenuItem>
      ),
    },
    {
      visible: showIdentificationItem,
      content: (
        <DropdownMenuItem
          onSelect={() => {
            setIdentificationDialogOpen(true);
          }}
          testId="identification-menu-item"
        >
          <MessageCheckSquare className="h-4 w-4" />
          Add identification
        </DropdownMenuItem>
      ),
    },
  ];

  return (
    <>
      <IdentificationDialog
        open={identificationDialogOpen}
        onOpenChange={setIdentificationDialogOpen}
        onSubmit={(data) => {
          form.setValue<"identificationDetails">(
            "identificationDetails",
            data,
            { shouldDirty: true },
          );
        }}
        initialData={
          form.getValues<"identificationDetails">("identificationDetails") ??
          undefined
        }
      >
        {identificationDetails != null && (
          <Tooltip>
            <DialogTrigger asChild onFocusCapture={stopEventPropagation}>
              <TooltipTrigger asChild>
                <Button
                  size="xs"
                  startIcon={MessageCheckSquare}
                  className={twMerge(
                    identificationFieldCount === 0
                      ? "text-grey-500"
                      : "py-1 pr-2.5",
                  )}
                  testId="identification-button"
                >
                  Identification{" "}
                  {identificationFieldCount > 0 && (
                    <Badge
                      color="grey"
                      type="icon"
                      className="rounded-md px-1.5"
                    >
                      {identificationFieldCount}
                    </Badge>
                  )}
                </Button>
              </TooltipTrigger>
            </DialogTrigger>
            <TooltipContent side="bottom" sideOffset={0}>
              Add identification
            </TooltipContent>
          </Tooltip>
        )}
      </IdentificationDialog>
      <FormField
        control={form.control}
        name="ipenNumber"
        render={({ field }) => (
          <Tooltip>
            <IpenNumberDialog
              initialValue={field.value}
              open={ipenNumberDialogOpen}
              onOpenChange={setIpenNumberDialogOpen}
              onSubmit={field.onChange}
            >
              {ipenNumber == null ? null : (
                <TooltipTrigger asChild>
                  <Button
                    size="xs"
                    startIcon={IpenNumber}
                    className={twMerge(
                      ipenNumber === "" ? "text-grey-500" : undefined,
                    )}
                    testId="ipen-number-button"
                  >
                    {ipenNumber === "" ? "IPEN number" : ipenNumber}
                  </Button>
                </TooltipTrigger>
              )}
            </IpenNumberDialog>
            <TooltipContent side="bottom" sideOffset={0}>
              Set IPEN number
            </TooltipContent>
          </Tooltip>
        )}
      />
      <Tooltip>
        <AccessionNumberDialog
          open={accessionNumberDialogOpen}
          onOpenChange={setAccessionNumberDialogOpen}
          initialValues={{
            accessionDate: form.getValues<"accessionDate">("accessionDate"),
            accessionNumberFormat: form.getValues<"accessionNumberFormat">(
              "accessionNumberFormat",
            ),
            exogenousAccessionNumber:
              form.getValues<"exogenousAccessionNumber">(
                "exogenousAccessionNumber",
              ),
            accessionNumberSuffix: form.getValues<"accessionNumberSuffix">(
              "accessionNumberSuffix",
            ),
          }}
          onSubmit={(data) => {
            form.setValue<"accessionNumberSuffix">(
              "accessionNumberSuffix",
              data.accessionNumberSuffix,
            );
            form.setValue<"exogenousAccessionNumber">(
              "exogenousAccessionNumber",
              data.exogenousAccessionNumber,
            );
            form.setValue<"accessionNumberFormat">(
              "accessionNumberFormat",
              data.accessionNumberFormat,
            );
            form.setValue<"generateAccessionNumber">(
              "generateAccessionNumber",
              false,
            );
          }}
        >
          {accessionNumber == null ? null : (
            <TooltipTrigger asChild>
              <Button
                size="xs"
                startIcon={Hash2}
                className={twMerge(
                  accessionNumber === "" ? "text-grey-500" : undefined,
                )}
                testId="accession-number-button"
              >
                {accessionNumber === "" ? "Accession number" : accessionNumber}
              </Button>
            </TooltipTrigger>
          )}
        </AccessionNumberDialog>
        <TooltipContent side="bottom" sideOffset={0}>
          Change accession number
        </TooltipContent>
      </Tooltip>
      <DropdownMenu>
        {menuItems.some((item) => item.visible) && (
          <Tooltip>
            <DropdownMenuTrigger asChild>
              <TooltipTrigger asChild>
                <IconButton
                  icon={DotsHorizontal}
                  ariaLabel="More fields"
                  size="xs"
                  className="text-grey-500"
                  testId="more-accession-fields-button"
                  onFocusCapture={stopEventPropagation}
                />
              </TooltipTrigger>
            </DropdownMenuTrigger>
            <TooltipContent side="bottom" sideOffset={0}>
              More actions
            </TooltipContent>
          </Tooltip>
        )}
        <DropdownMenuContent>
          {menuItems
            .filter((item) => item.visible)
            .map((item, index, filteredItems) => (
              // eslint-disable-next-line react/no-array-index-key
              <Fragment key={index}>
                {item.content}
                {index < filteredItems.length - 1 && <DropdownMenuSeparator />}
              </Fragment>
            ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
